import { useEffect } from "react";

import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { useLocation } from "@redwoodjs/router";

import {
  sidebarFrequentlyUsedSeedIds,
  sidebarReportPageById,
  sidebarReportPages,
} from "src/lib/sidebarNavigation";

const STORE_VERSION = 1;
const MAX_FREQUENTLY_USED_ITEMS = 3;
const defaultFrequentlyUsedStore = {
  version: STORE_VERSION,
  visits: {},
};

const sidebarReportPageIndexById = Object.fromEntries(
  sidebarReportPages.map((page, index) => [page.id, index])
);

export const frequentlyUsedAtom = atomWithStorage(
  "sidebarFrequentlyUsed",
  defaultFrequentlyUsedStore
);

export const lastTrackedPathAtom = atom(null);

export const normalizeFrequentlyUsedStore = (store) => {
  if (
    !store ||
    store.version !== STORE_VERSION ||
    !store.visits ||
    typeof store.visits !== "object" ||
    Array.isArray(store.visits)
  ) {
    return defaultFrequentlyUsedStore;
  }

  const visits = Object.entries(store.visits).reduce((acc, [pageId, visit]) => {
    if (!sidebarReportPageById[pageId] || !visit || typeof visit !== "object") {
      return acc;
    }

    const visitCount = Number(visit.visitCount);
    const lastVisitedAt =
      typeof visit.lastVisitedAt === "string" ? visit.lastVisitedAt : null;

    if (
      !Number.isFinite(visitCount) ||
      visitCount <= 0 ||
      !lastVisitedAt ||
      Number.isNaN(Date.parse(lastVisitedAt))
    ) {
      return acc;
    }

    acc[pageId] = {
      lastVisitedAt,
      visitCount,
    };

    return acc;
  }, {});

  return {
    version: STORE_VERSION,
    visits,
  };
};

export const incrementFrequentlyUsedVisit = (store, pageId, nowIso) => {
  const normalizedStore = normalizeFrequentlyUsedStore(store);

  if (!sidebarReportPageById[pageId]) {
    return normalizedStore;
  }

  const currentVisit = normalizedStore.visits[pageId];

  return {
    version: STORE_VERSION,
    visits: {
      ...normalizedStore.visits,
      [pageId]: {
        lastVisitedAt: nowIso,
        visitCount: (currentVisit?.visitCount || 0) + 1,
      },
    },
  };
};

export const selectFrequentlyUsedItems = (store) => {
  const normalizedStore = normalizeFrequentlyUsedStore(store);
  const rankedPageIds = Object.entries(normalizedStore.visits)
    .filter(([pageId]) => sidebarReportPageById[pageId])
    .sort(([pageIdA, visitA], [pageIdB, visitB]) => {
      if (visitA.visitCount !== visitB.visitCount) {
        return visitB.visitCount - visitA.visitCount;
      }

      const timestampA = Date.parse(visitA.lastVisitedAt);
      const timestampB = Date.parse(visitB.lastVisitedAt);

      if (timestampA !== timestampB) {
        return timestampA - timestampB;
      }

      return (
        sidebarReportPageIndexById[pageIdA] -
        sidebarReportPageIndexById[pageIdB]
      );
    })
    .map(([pageId]) => pageId)
    .slice(0, MAX_FREQUENTLY_USED_ITEMS);

  const selectedPageIds = [...rankedPageIds];

  for (const seedId of sidebarFrequentlyUsedSeedIds) {
    if (selectedPageIds.length >= MAX_FREQUENTLY_USED_ITEMS) {
      break;
    }

    if (!selectedPageIds.includes(seedId) && sidebarReportPageById[seedId]) {
      selectedPageIds.push(seedId);
    }
  }

  return selectedPageIds
    .map((pageId) => sidebarReportPageById[pageId])
    .filter(Boolean);
};

export const shouldTrackFrequentlyUsedVisit = ({
  pageId,
  pathname,
  lastTrackedPath,
}) => {
  return Boolean(
    pageId &&
      pathname &&
      sidebarReportPageById[pageId] &&
      pathname !== lastTrackedPath
  );
};

const recordFrequentlyUsedVisitAtom = atom(
  null,
  (get, set, { nowIso, pageId, pathname }) => {
    const lastTrackedPath = get(lastTrackedPathAtom);

    if (
      !shouldTrackFrequentlyUsedVisit({ pageId, pathname, lastTrackedPath })
    ) {
      return;
    }

    set(lastTrackedPathAtom, pathname);
    set(frequentlyUsedAtom, (store) =>
      incrementFrequentlyUsedVisit(store, pageId, nowIso)
    );
  }
);

export const useFrequentlyUsedSidebarItems = () => {
  const [store] = useAtom(frequentlyUsedAtom);

  return selectFrequentlyUsedItems(store);
};

export const useTrackFrequentlyUsedPage = (pageId) => {
  const { pathname } = useLocation();
  const [, recordVisit] = useAtom(recordFrequentlyUsedVisitAtom);

  useEffect(() => {
    recordVisit({
      nowIso: new Date().toISOString(),
      pageId,
      pathname,
    });
  }, [pageId, pathname, recordVisit]);
};
